import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, TemplateRef, ViewChild} from '@angular/core';
import {TranslocoService} from "@ngneat/transloco";
import {ToastrService} from 'ngx-toastr';
import {faFileUpload} from "@fortawesome/free-solid-svg-icons";
import {AccessService, AppService, LocalStoreService, StoreSelectorsService} from "../../services";
import {formatBytes, LIMIT_FREE_STORAGE, LIMIT_SUBSCRIBE_STORAGE, LIMIT_UPLOADS_COUNT, restrictSingleQuote, SUPPORTED_FILE_TYPES} from "../../common";
import {actionAddResources} from '../../store/actions/user.actions';
import {SpecialCompbaser} from "../compbaser";
import {FileObj, IAccessTable, ICRUDRecord, IResource, IResourceStatistic, IResourceUpload, IUser, UploadStatus} from "../../interfaces";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Lib} from "../../../libs/Lib";

interface IFileInfo {
    fileId: string;
    fileSize: number;
    progress: number;
}

@Component({
    selector: 'app-resource-upload',
    templateUrl: 'resource-upload.component.html',
    styleUrls: ['resource-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceUploadComponent extends SpecialCompbaser {
    @Input() showHelpButton = false;

    @Input() category = '';

    readonly faFileUpload = faFileUpload;
    readonly limitUploadsCount = LIMIT_UPLOADS_COUNT;

    @ViewChild('fileDropRef') fileDropRef: ElementRef;
    @ViewChild("uploadModal", {read: TemplateRef}) uploadModal: TemplateRef<any>;

    uploadModalRef: MatDialogRef<any>;
    title = 'resumeMultipleUpload';
    selectedFiles: Array<FileObj> = [];
    accessConfig: IAccessTable;
    supportedFiles: string = SUPPORTED_FILE_TYPES.join(', ');
    fileExtension: string = SUPPORTED_FILE_TYPES.map((v) => '.' + v).join();
    resourceStatistics: IResourceStatistic;
    user: IUser;
    uploading = false;
    uploadPercentage = 0;
    totalFileSize = 0;
    formattedTotalSize = '0 Byte';
    formattedUploadingSize = '0 Byte';
    copyFileObjects: Array<IFileInfo> = [];

    constructor(
        private storeSelector: StoreSelectorsService,
        private appService: AppService,
        private translocoService: TranslocoService,
        private toastr: ToastrService,
        private localStoreService: LocalStoreService,
        private accessService: AccessService,
        private cd: ChangeDetectorRef,
        dialog: MatDialog) {
        super(dialog);

        this.cod(
            this.accessService.getGroupsTable('SignStudio Resources', "resources").subscribe((v) => {
                this.accessConfig = v;
            })
        );
        this.cod(
            this.storeSelector.getUser()
                .subscribe((i_user: IUser) => {
                    this.user = i_user;
                })
        );
        this.cod(
            this.storeSelector.getStorageStatistics().subscribe((v: IResourceStatistic) => {
                this.resourceStatistics = v;
                this.cd.markForCheck();
            })
        );
    }

    /** on file drop handler */
    onFileDropped($event) {
        this.prepareFilesList($event);
    }

    /** handle file from browsing */
    fileBrowseHandler(files) {
        this.prepareFilesList(files);
    }

    /** upload selected files */
    uploadFiles() {
        if (!this.accessConfig.add) return this.accessService.showNoAccess();
        if (Lib.IsAccessBlocked(this.user)) {
            return this.toastr.error(this.translocoService.translate('toast.accountDisabled', {}, this.translocoService.getActiveLang()), '[ Contact live chat ]');
        }
        const cloudMaxStorage = this.user.enterprise ? LIMIT_SUBSCRIBE_STORAGE : LIMIT_FREE_STORAGE;
        const usedStorage = this.resourceStatistics?.dataStoragePercent?.[1].value;

        if (usedStorage >= cloudMaxStorage) {
            this.toastr.error(this.translocoService.translate('toast.noStorageLeft', {}, this.translocoService.getActiveLang()));
            return;
        }
        if (!this.selectedFiles || this.selectedFiles.length == 0) {
            this.toastr.error(this.translocoService.translate('toast.noUploadFile', {}, this.translocoService.getActiveLang()));
            return;
        }
        if (this.selectedFiles.filter((obj) => obj.uploadStatus !== UploadStatus.SUCCESS).length == 0) {
            return;
        }

        this.uploading = true;
        this.showUploadingWaitDialog();
        this.resetFileInput();
        this.uploadSelectedFiles();
        this.cd.detectChanges();
    }

    /** delete file by file id */
    deleteFile(fileId) {
        this.selectedFiles.splice(this.selectedFiles.findIndex((file) => file.fileId == fileId), 1);
        this.cd.detectChanges();
    }

    clearList() {
        this.selectedFiles = [];
        this.uploading = false;
        this.closeUploadDialog();
        this.resetFileInput();
        this.cd.detectChanges();
    }

    private calcTotalSize() {
        this.totalFileSize = this.copyFileObjects.reduce((size, fileObj) => {
            return size + fileObj.fileSize;
        }, 0);
        this.formattedTotalSize = formatBytes(this.totalFileSize, 2);
    }

    private calcUploadingPercentage() {
        const uploadedFileSize = this.copyFileObjects.reduce((size, fileObj) => {
            const f = this.selectedFiles.find((_fileObj) => _fileObj.fileId == fileObj.fileId);
            let progress = 100;
            if (f) progress = f.progress;

            return size + fileObj.fileSize * progress;
        }, 0);
        this.formattedUploadingSize = formatBytes(uploadedFileSize / 100, 2);

        if (!this.totalFileSize || !uploadedFileSize) {
            this.uploadPercentage = 0;
        } else {
            this.uploadPercentage = Math.round(uploadedFileSize / this.totalFileSize);
        }
        this.cd.detectChanges();
    }

    private async isValidSVG(file) {
        if (!file || !file.name) return true;

        const ext = file.name.split('.').pop();
        if (!ext || ext.toLowerCase() != "svg") return true;

        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => {
                try {
                    const parser = new DOMParser();
                    const domSVG = parser.parseFromString(String(fr.result), "image/svg+xml").documentElement;

                    // check if SVG contains external HTTP
                    if (domSVG.getElementsByTagName('foreignObject').length > 0) {
                        resolve(false);
                    }

                } catch (err) {
                } finally {
                    resolve(true);
                }
            };

            fr.readAsText(file);
        });
    }

    /** Convert Files list to normal array list */
    private async prepareFilesList(files: Array<any>) {
        for (const file of files) {
            if (!await this.isValidSVG(file)) {
                this.toastr.warning(this.translocoService.translate('toast.invalidSVG', {}, this.translocoService.getActiveLang()));
                continue;
            }

            if (this.selectedFiles.length >= LIMIT_UPLOADS_COUNT) {
                this.toastr.warning(this.translocoService.translate('toast.limitUploads', {}, this.translocoService.getActiveLang()));
                break;
            }
            const fileName = restrictSingleQuote(file.name).split('.').slice(0, -1).join('.');
            const fileExt = file.name.split('.').pop();

            const obj = {
                fileName,
                fileType: fileExt,
                file,
                fileId: `${fileName}-${file.lastModified}`,
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(file.size, 2),
                fileSize: parseFloat(file.size)
            };
            this.selectedFiles.push(obj);
        }
        // this.showUploadingWaitDialog();
        this.cd.detectChanges();
    }

    private uploadSelectedFiles() {
        const validUploadFiles = this.selectedFiles.filter((fileObj) =>
            fileObj.uploadStatus == UploadStatus.READY || fileObj.uploadStatus == UploadStatus.FAILED
        );

        for (const fileObj of validUploadFiles) {

            const file: File = fileObj.file;
            const formData = new FormData();
            formData.append("thumbnail", file);

            const saveRecord: ICRUDRecord = {
                studioId: this.localStoreService.getStorageConfig().studioId,
                command: 'upload',
                primary: 'resource_id',
                table: 'resources',
                body: formData,
                id: null,
                options: {noStringify: true, reportProgress: true},
            };

            // this.storeSelector.dispatch(actionUploadFiles({
            //     saveRecord: saveRecord,
            //     file: fileObj
            // }));

            this.appService.uploadFileWithProcess(saveRecord, (i_crudReply) => {
                const reply = i_crudReply.serverReply;
                if (i_crudReply.command == 'progress' || (reply && reply.hasOwnProperty('type') && reply.hasOwnProperty('body') == false)) {
                    if (reply['type'] == '1' && reply['total']) {
                        const percent = Math.round(100 * parseFloat(reply['loaded']) / parseFloat(reply['total']));
                        if (percent < 100) {
                            fileObj.progress = percent;
                        } else {
                            fileObj.progress = 100;
                        }
                    }
                    fileObj.uploadStatus = UploadStatus.INPROGRESS;
                } else {
                    if (!reply?.status) {
                        fileObj.progress = 0;
                        fileObj.uploadStatus = UploadStatus.FAILED;
                    } else {
                        fileObj.progress = 100;
                        fileObj.uploadStatus = UploadStatus.SUCCESS;
                        fileObj.fileId = reply['body'].id;
                    }
                    this.updateResource();
                }
                this.calcUploadingPercentage();
            });
        }
    }

    private resetFileInput() {
        if (this.fileDropRef) {
            this.fileDropRef.nativeElement.value = "";
        }
    }

    private async updateResource() {
        let uploadingNow = false;
        let duration = '0';
        for (const fileObj of this.selectedFiles) {
            if (fileObj.uploadStatus == UploadStatus.SUCCESS) {
                if (SUPPORTED_FILE_TYPES.indexOf(fileObj.fileType.toLowerCase()) === -1) continue;

                if (fileObj.fileType === 'mp4' || fileObj.fileType === 'MP4') {
                    duration = await this.getVideoDuration(fileObj);
                }
                const updatedRes: IResourceUpload = {
                    resource_name: fileObj.fileName,
                    resource_type: fileObj.fileType,
                    default_player: this.storeSelector.getBlockLabelByFileType(fileObj.fileType.toLowerCase()).id as any,
                    resource_total_time: duration,
                    resource_date_created: new Date().getTime() + '',
                    resource_date_modified: new Date().getTime() + '',
                    resource_bytes_total: fileObj.fileSize + '',
                    access_key: '0',
                    tree_path: this.category
                };

                /** update resource of table */
                const saveRecord: ICRUDRecord = {
                    studioId: this.localStoreService.getStorageConfig().studioId,
                    command: 'update',
                    primary: 'resource_id',
                    table: 'resources',
                    body: updatedRes,
                    id: fileObj.fileId
                };

                // this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
                fileObj.uploadStatus = UploadStatus.UPDATING;

                this.appService.crudRecord(saveRecord).then((i_crudReply: ICRUDRecord) => {
                    /** important don't update store since server command failed **/
                    if (!i_crudReply.serverReply.status) return;

                    /** add new resource in store */
                    updatedRes['resource_id'] = String(fileObj.fileId);
                    updatedRes['id'] = String(fileObj.fileId);
                    updatedRes['changelist_id'] = String(i_crudReply.serverReply.changelist_id);
                    this.storeSelector.dispatch(actionAddResources({data: updatedRes as IResource}));
                    this.deleteFile(fileObj.fileId);
                });
            }

            if (fileObj.uploadStatus == UploadStatus.INPROGRESS) {
                uploadingNow = true;
            }
        }
        this.uploading = uploadingNow;
        if (uploadingNow === false) {
            setTimeout(() => {
                this.closeUploadDialog();
            }, 500);
        }
        this.cd.detectChanges();
    }

    private showUploadingWaitDialog() {
        this.copyFileObjects = this.selectedFiles.map((fileObj) => ({
            fileId: fileObj.fileId,
            fileSize: fileObj.fileSize,
            progress: fileObj.progress
        }));

        this.calcTotalSize();
        this.uploadPercentage = 0;
        this.formattedUploadingSize = '0 Byte';

        this.uploadModalRef = this.dialog.open(this.uploadModal, {
            height: '520px',
            width: '500px',
            panelClass: 'transparentDialog',
            data: {}
        });
    }

    private closeUploadDialog() {
        if (this.uploadModalRef) {
            this.uploadModalRef.close();
        }
        this.copyFileObjects = [];
    }

    private async getVideoDuration(i_file): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => {
                    window.URL.revokeObjectURL(video.src);
                    resolve(Lib.ParseToFloatDouble(video.duration));
                };
                video.onerror = () => {
                    // workaround 2-14-2024
                    // reject("Invalid video. Please select a video file.");
                    return resolve(0);
                };
                video.src = URL.createObjectURL(i_file.file);

            } catch (e) {
                reject(e);
            }
        });
    }

    override destroy(): void {
        super.destroy();
        this.uploadModal = null;
        this.selectedFiles = [];
    }
}
